import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectsService } from '../../../../../core/projects.service';
import { FetchContractorsListRequest } from '../../../../../core/projects.actions';
import { contractorsListInfo } from '../../../../../core/projects.selectors';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-warranty-package-form',
  templateUrl: './warranty-package-form.component.html',
  styleUrls: ['./warranty-package-form.component.scss'],
  standalone: false,
})
export class WarrantyPackageFormComponent implements OnInit, OnDestroy {
  @Input() data: any;
  form: FormGroup;
  isFormDirty = false;
  isSaving = false;

  errorMessages: Record<string, string> = {};

  warrantyOptions: any[] = [];
  contractorOptions: any[] = [];
  claims: any[] = [];

  // Claim picker
  showClaimPicker = false;
  availableClaims: any[] = [];
  availableClaimsLoading = false;

  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store,
    private projectsService: ProjectsService,
    private appHttpService: AppHttpService,
    private dialog: MatDialog,
  ) {
    this.form = new FormGroup({
      title: new FormControl('', Validators.required),
      description: new FormControl(''),
      warranty_id: new FormControl(''),
      sent_to_contractor_id: new FormControl(''),
      due_date: new FormControl(''),
    });
  }

  ngOnInit() {
    // Build warranty options
    if (this.data?.warranties?.length) {
      this.warrantyOptions = this.data.warranties.map(w => ({
        value: w.id,
        label: (w.attributes?.name || w.name || w.id) + (w.attributes?.status ? ` (${w.attributes.status})` : ''),
      }));
      if (this.warrantyOptions.length === 1) {
        this.form.patchValue({ warranty_id: this.warrantyOptions[0].value });
      }
    }

    // Fetch contractors and filter to primes
    this.store.dispatch(FetchContractorsListRequest({ payload: { projectId: this.data.projectId } }));
    this.store.select(contractorsListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.list) {
          this.contractorOptions = data.list
            .filter(c => c.prime)
            .map(c => ({
              value: c.id,
              label: c.name || c.poc || c.id,
            }));
          // Auto-select if only one prime contractor
          if (this.contractorOptions.length === 1 && !this.form.value.sent_to_contractor_id) {
            this.form.patchValue({ sent_to_contractor_id: this.contractorOptions[0].value });
          }
        }
      });

    // Pre-populate claims from selected claims or from editing an existing package
    if (this.data?.selectedClaims?.length) {
      this.claims = [...this.data.selectedClaims];
    }

    // Editing an existing draft package
    if (this.data?.editPackage) {
      const pkg = this.data.editPackage;
      this.form.patchValue({
        title: pkg.title || '',
        description: pkg.description || '',
        warranty_id: pkg.warranty_id || '',
        sent_to_contractor_id: pkg.sent_to_contractor_id || '',
        due_date: pkg.due_date || '',
      });
      // Load items from package detail
      if (pkg.uuid || pkg.id) {
        this.loadPackageDetail(pkg.uuid || pkg.id);
      }
    }

    this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.isFormDirty = this.form.dirty;
      });
  }

  loadPackageDetail(packageId: number) {
    this.appHttpService.getService(`/v1/warranty/claim-packages/${packageId}`).subscribe({
      next: (resp: any) => {
        const pkg = resp?.data || resp;
        if (pkg?.claims?.length) {
          this.claims = pkg.claims.map(claim => ({
            id: claim.id,
            claim_number: claim.claim_number,
            title: claim.title,
            severity: claim.severity,
            status: claim.status,
          }));
        }
        // Patch any missing fields
        if (pkg.warranty_id && !this.form.value.warranty_id) {
          this.form.patchValue({ warranty_id: pkg.warranty_id });
        }
        if (pkg.sent_to_contractor_id && !this.form.value.sent_to_contractor_id) {
          this.form.patchValue({ sent_to_contractor_id: pkg.sent_to_contractor_id });
        }
      },
    });
  }

  removeClaim(claim: any) {
    this.claims = this.claims.filter(c => c.id !== claim.id);
    this.isFormDirty = true;
  }

  toggleClaimPicker() {
    this.showClaimPicker = !this.showClaimPicker;
    if (this.showClaimPicker && this.availableClaims.length === 0) {
      this.fetchAvailableClaims();
    }
  }

  fetchAvailableClaims() {
    if (!this.data?.projectId) return;
    this.availableClaimsLoading = true;
    // Fetch all claims for all warranties in this project
    const warrantyId = this.form.value.warranty_id || (this.warrantyOptions.length === 1 ? this.warrantyOptions[0].value : '');
    if (!warrantyId) {
      // If no warranty selected, try to fetch from all warranties
      this.availableClaimsLoading = false;
      return;
    }
    this.appHttpService.getService(
      `/v1/warranty/project/${this.data.projectId}/warranties/${warrantyId}/claims`,
      { from: 0, total: 100 },
    ).subscribe({
      next: (resp: any) => {
        const claimsList = resp?.data || [];
        this.availableClaims = claimsList
          .map(c => {
            const attrs = c.attributes || c;
            const pics = attrs.pictures || [];
            return {
              id: c.id || attrs.id,
              claim_number: attrs.claim_number,
              title: attrs.title,
              description: attrs.description,
              severity: attrs.severity,
              status: attrs.status,
              claim_type: attrs.claim_type,
              structure_type: attrs.structure_type,
              defect_type: attrs.defect_type,
              pictures: pics.map(p => ({
                id: p.id,
                thumb_url: p.attributes?.thumb_url || p.thumb_url,
                original_url: p.attributes?.original_url || p.original_url,
              })),
            };
          })
          .filter(c => !['draft', 'closed', 'verified'].includes(c.status));
        this.availableClaimsLoading = false;
      },
      error: () => {
        this.availableClaims = [];
        this.availableClaimsLoading = false;
      },
    });
  }

  isClaimAlreadyAdded(claimId: number): boolean {
    return this.claims.some(c => c.id === claimId);
  }

  addClaim(claim: any) {
    if (!this.isClaimAlreadyAdded(claim.id)) {
      this.claims = [...this.claims, claim];
      this.isFormDirty = true;
    }
  }

  checkForErrors(event: any) {
    if (event?.controlName && event?.error) {
      this.errorMessages[event.controlName] = event.error;
    }
  }

  severityMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
    low: { label: 'Low', bgColor: 'bg-low', textColor: 'txt-low' },
    medium: { label: 'Medium', bgColor: 'bg-medium', textColor: 'txt-medium' },
    high: { label: 'High', bgColor: 'bg-high', textColor: 'txt-high' },
    critical: { label: 'Critical', bgColor: 'bg-critical', textColor: 'txt-critical' },
  };

  save() {
    if (this.data?.editPackage?.id) {
      this._update();
    } else {
      this._create();
    }
  }

  send() {
    if (this.data?.editPackage?.id) {
      // Save first, then send
      this._update(() => {
        this._sendPackage(this.data.editPackage.uuid || this.data.editPackage.id);
      });
    } else {
      this._create((newUuid: string) => {
        this._sendPackage(newUuid);
      });
    }
  }

  private _buildPayload() {
    const val = this.form.value;
    return {
      title: val.title,
      description: val.description || '',
      warranty_id: val.warranty_id || undefined,
      sent_to_contractor_id: val.sent_to_contractor_id || undefined,
      due_date: val.due_date || undefined,
      claims: this.claims.map(c => c.id),
    };
  }

  private _create(onSuccess?: (uuid: string) => void) {
    if (!this.form.valid || !this.data?.projectId) return;
    this.isSaving = true;
    this.appHttpService.postService(
      `/v1/warranty/project/${this.data.projectId}/claim-packages`,
      this._buildPayload(),
    ).subscribe({
      next: (resp: any) => {
        this.isSaving = false;
        const data = resp?.data || resp;
        const newUuid = data?.attributes?.uuid || data?.uuid || data?.id;
        if (onSuccess && newUuid) {
          onSuccess(newUuid);
        } else {
          this.projectsService.closeRightPanel.next();
        }
      },
      error: () => {
        this.isSaving = false;
      },
    });
  }

  private _update(onSuccess?: () => void) {
    if (!this.form.valid) return;
    this.isSaving = true;
    const pkgId = this.data.editPackage.uuid || this.data.editPackage.id;
    // Delete and recreate the draft
    this.appHttpService.deleteService(`/v1/warranty/claim-packages/${pkgId}`).subscribe({
      next: () => {
        this.appHttpService.postService(
          `/v1/warranty/project/${this.data.projectId}/claim-packages`,
          this._buildPayload(),
        ).subscribe({
          next: (resp: any) => {
            this.isSaving = false;
            const newData = resp?.data || resp;
            if (onSuccess) {
              this.data.editPackage.id = newData?.id || newData?.attributes?.id;
              this.data.editPackage.uuid = newData?.uuid || newData?.attributes?.uuid;
              onSuccess();
            } else {
              this.projectsService.closeRightPanel.next();
            }
          },
          error: () => {
            this.isSaving = false;
          },
        });
      },
      error: () => {
        this.isSaving = false;
      },
    });
  }

  private _sendPackage(packageId: string | number) {
    this.isSaving = true;
    this.appHttpService.postService(
      `/v1/warranty/claim-packages/${packageId}/send`,
      {},
    ).subscribe({
      next: () => {
        this.isSaving = false;
        this.projectsService.closeRightPanel.next();
      },
      error: () => {
        this.isSaving = false;
      },
    });
  }

  deletePackage() {
    if (!this.data?.editPackage?.id) return;
    const dialogData = new ConfirmDialogModel('Delete Package', 'Delete this draft package? This cannot be undone.', 'Delete', 'Cancel');
    this.dialog.open(ConfirmDialogComponent, { disableClose: true, data: dialogData })
      .afterClosed().subscribe(result => {
        if (!result) return;
        this.appHttpService.deleteService(`/v1/warranty/claim-packages/${this.data.editPackage.uuid || this.data.editPackage.id}`).subscribe({
          next: () => {
            this.projectsService.closeRightPanel.next();
          },
        });
      });
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
